import React from 'react';
import Card from '../Card/Card';
import { Segmented } from 'ps-design';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import './GeoDifferentialCard.scss';
import Select from 'ps-components/ReactSelect/ReactSelect';
import { DIFFERENTIAL_TYPE } from '../SkillGeoDifferentialReducer';
import { numberWithCommas } from 'ps-components/lib/formatters';
import { toLocationDisplayValue } from '../helper';
import ReactSelectAsync from 'ps-components/ReactSelectAsync/ReactSelectAsync';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import debounce from 'lodash/debounce';

const GEO_JOB_AND_LOCATION_DESCRIPTION = 'Find out how much this job family is paid differently based on location';
const GEO_LOCATION_ONLY_DESCRIPTION = 'Find out what the cost of labor is based on location';
const GEO_COLLAPSED_CARD_DESCRIPTION = `${GEO_LOCATION_ONLY_DESCRIPTION}, or how job family pay differs based on location`;
const GEO_JOB_AND_LOCATION_TOGGLE_LABEL = 'Location + Job Family';
const GEO_LOCATION_ONLY_TOGGLE_LABEL = 'Location Only';
const DROPDOWN_SAMPLE_MESSAGE =
  'Locations are limited for preview.  If you are interested in getting access to the 17,000+ geo locations, please click "Learn More" in the bottom bar.';

export class GeoDifferentialCard extends React.Component {
  static propTypes = {
    isAdded: bool,
    isExpanded: bool,
    locationOnly: bool,
    toggleGeoCard: func,
    toggleGeoCardExpand: func,
    toggleGeoLocationOnly: func,
    jobFamilies: arrayOf(string),
    jobLocations: arrayOf(
      shape({
        locationCity: string,
        locationState: string
      })
    ),
    getGeoDifferential: func,
    getJobFamilies: func,
    jobFamiliesLoading: bool,
    differential: number,
    differentialOverride: number,
    updateDifferentialOverride: func,
    differentialLoading: bool,
    differentialFailed: bool,
    clearDifferential: func,
    selectedJobFamily: string,
    selectedJobLocation: string,
    setSelectedJobFamily: func,
    setSelectedJobLocation: func,
    resetGeoState: func,
    getJobLocations: func,
    isRemoved: bool,
    setGeoDifferentialIsRemoved: func,
    pricingType: string,
    isApplied: bool,
    sampleSize: number,
    setIsPendingRemove: func,
    isPendingRemove: bool,
    disabled: bool,
    featureIsPurchased: bool
  };

  constructor(props) {
    super(props);
    this.loadJobFamilyOptions = this.loadJobFamilyOptions.bind(this);
    this.onInputValueChange = this.onInputValueChange.bind(this);
    this.state = { locationInputValue: '' };
  }

  componentDidMount() {
    const { featureIsPurchased, getJobLocations } = this.props;

    getJobLocations(featureIsPurchased);
  }

  componentDidUpdate(prevProps) {
    const {
      resetGeoState,
      isAdded,
      featureIsPurchased,
      getJobLocations,
      getGeoDifferential,
      getJobFamilies,
      clearDifferential,
      selectedJobLocation,
      selectedJobFamily,
      setSelectedJobLocation
    } = this.props;

    if (!isAdded && prevProps.isAdded) {
      resetGeoState();
    } else if (isAdded && !prevProps.isAdded) {
      getJobLocations(featureIsPurchased);

      if (!featureIsPurchased) setSelectedJobLocation('Seattle, Washington');
    } else if (
      selectedJobLocation !== prevProps.selectedJobLocation ||
      selectedJobFamily !== prevProps.selectedJobFamily
    ) {
      if (selectedJobLocation) {
        getGeoDifferential(selectedJobLocation, selectedJobFamily);
        getJobFamilies(selectedJobLocation);
      } else {
        clearDifferential();
      }
    }
  }

  renderSegmentedToggle = () => {
    const { locationOnly } = this.props;
    const description = locationOnly ? GEO_LOCATION_ONLY_DESCRIPTION : GEO_JOB_AND_LOCATION_DESCRIPTION;
    return (
      <React.Fragment>
        <div className="card__row vertical-padding">
          <Segmented
            className="pxl-segmented-primary geo-differential__segmented"
            onChange={this.toggleLocationOnly}
            options={[
              {
                text: GEO_LOCATION_ONLY_TOGGLE_LABEL,
                value: true
              },
              {
                text: GEO_JOB_AND_LOCATION_TOGGLE_LABEL,
                value: false
              }
            ]}
            value={locationOnly}
          />
        </div>
        <div className="card__row vertical-padding">
          <p className="pxl-text-muted">{description}</p>
        </div>
      </React.Fragment>
    );
  };

  toggleLocationOnly = () => {
    const { toggleGeoLocationOnly } = this.props;
    toggleGeoLocationOnly();
  };

  jobFamilyChange = jobFamilyOption => {
    const { setSelectedJobFamily } = this.props;
    const jobFamily = jobFamilyOption ? jobFamilyOption.value : null;
    setSelectedJobFamily(jobFamily);
  };

  jobLocationChange = jobLocationOption => {
    const { setSelectedJobLocation } = this.props;
    const jobLocation = jobLocationOption ? jobLocationOption.value : null;
    setSelectedJobLocation(jobLocation);
  };

  renderJobFamilySelect = () => {
    const { selectedJobLocation, jobFamiliesLoading, selectedJobFamily } = this.props;
    const jobFamilyOptions = this.getJobFamilyOptions();
    const jobFamily = selectedJobFamily ? jobFamilyOptions.filter(({ value }) => value === selectedJobFamily) : null;

    return (
      <div className="job-family-select-group">
        <b>Job Family</b>
        <Select
          className="job-family__select"
          options={jobFamilyOptions}
          placeholder="Search for job family"
          getOptionLabel={option => option.label}
          getOptionValue={option => option.value}
          onChange={this.jobFamilyChange}
          classNamePrefix="react-select"
          isDisabled={!selectedJobLocation}
          isLoading={jobFamiliesLoading}
          value={jobFamily}
          noOptionsMessage={() => 'No job family available for this location'}
          isClearable={true}
        />
      </div>
    );
  };

  renderCollapsedView = () => {
    return (
      <div className="card__row vertical-padding">
        <p className="pxl-text-muted">{GEO_COLLAPSED_CARD_DESCRIPTION}</p>
      </div>
    );
  };

  renderExpandedView = () => {
    const { featureIsPurchased, jobLocations, locationOnly, selectedJobLocation, sampleSize } = this.props;
    const { locationInputValue } = this.state;

    const segmentedToggle = this.renderSegmentedToggle();
    let dropdownLocations = [];
    if (!featureIsPurchased) {
      dropdownLocations = jobLocations.map(location =>
        toLocationDisplayValue(location.locationCity, location.locationState)
      );
    }

    return (
      <React.Fragment>
        {segmentedToggle}
        <div className="card__row vertical-padding geo-search-group">
          <div>
            <b>Location</b>
            {!featureIsPurchased ? (
              <Dropdown
                className="job-location__dropdown"
                dropdownClass="job-location__dropdown-items"
                itemOnClick={this.handleDropdownClick}
                staticMessage={DROPDOWN_SAMPLE_MESSAGE}
                dropdownItems={dropdownLocations}
                text={selectedJobLocation}
                type="input"
              />
            ) : (
              <ReactSelectAsync
                className="job-location__select"
                classNamePrefix="react-select"
                placeholder="Search for location"
                noOptionsMessage={this.renderNoOptionsMessage}
                value={selectedJobLocation ? { value: selectedJobLocation, label: selectedJobLocation } : undefined}
                inputValue={locationInputValue}
                onInputChange={this.onInputValueChange}
                onChange={this.jobLocationChange}
                loadOptions={this.loadJobFamilyOptions}
                isClearable={true}
              />
            )}
          </div>
          {!locationOnly && this.renderJobFamilySelect()}
          {sampleSize && (
            <div className="sample-size__container">
              <div>Sample Size:</div>
              <div>{numberWithCommas(sampleSize)} profiles</div>
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  handleDropdownClick = e => {
    const { setSelectedJobLocation } = this.props;

    setSelectedJobLocation(e);
  };

  renderNoOptionsMessage(locationSelect) {
    return locationSelect.inputValue
      ? 'Sorry, no geo differential data is available for this location'
      : 'Type to search';
  }

  onInputValueChange(value) {
    this.setState({ locationInputValue: value });
  }

  getJobFamilyOptions = () => {
    const { jobFamilies } = this.props;
    return jobFamilies.map(jobFamily => {
      return { value: jobFamily, label: jobFamily };
    });
  };

  getLocationOptions = () => {
    const { jobLocations } = this.props;
    return jobLocations.map(jobLocation => {
      const cityState = toLocationDisplayValue(jobLocation.locationCity, jobLocation.locationState);
      return { value: cityState, label: cityState };
    });
  };

  loadJobFamilyOptions = debounce((value, callback) => {
    const { featureIsPurchased, getJobLocations } = this.props;
    getJobLocations(featureIsPurchased, value).then(jobLocations => {
      const options = jobLocations.map(jobLocation => {
        const displayValue = toLocationDisplayValue(jobLocation.locationCity, jobLocation.locationState);
        return { value: displayValue, label: displayValue };
      });
      callback(options);
    });
  }, 750);

  render() {
    const {
      isAdded,
      isExpanded,
      toggleGeoCard,
      toggleGeoCardExpand,
      differential,
      differentialOverride,
      updateDifferentialOverride,
      differentialLoading,
      differentialFailed,
      selectedJobFamily,
      selectedJobLocation,
      isRemoved,
      setGeoDifferentialIsRemoved,
      isApplied,
      pricingType,
      setIsPendingRemove,
      isPendingRemove,
      disabled
    } = this.props;

    return (
      <Card
        icon="icon-location"
        id="geo"
        title="Geo Differential"
        toggleDifferential={toggleGeoCard}
        toggleExpand={toggleGeoCardExpand}
        isAdded={isAdded}
        isExpanded={isExpanded}
        templateAfterAdd={this.renderExpandedView()}
        templateBeforeAdd={this.renderCollapsedView()}
        totalAdjustment={differential}
        totalAdjustmentOverride={differentialOverride}
        jobFamily={selectedJobFamily}
        location={selectedJobLocation}
        updateTotalAdjustmentOverride={updateDifferentialOverride}
        differentialLoading={differentialLoading}
        differentialFailed={differentialFailed}
        isRemoved={isRemoved}
        setRemoved={setGeoDifferentialIsRemoved}
        isApplied={isApplied}
        pricingType={pricingType}
        differentialType={DIFFERENTIAL_TYPE.GEO}
        setIsPendingRemove={setIsPendingRemove}
        isPendingRemove={isPendingRemove}
        disabled={disabled}
      />
    );
  }
}

export default GeoDifferentialCard;
